import {
  ContentsHeader,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  TextField,
} from "@graffer-inc/console-design-kit";
import { SmartApplyTemplate } from "../../../../template/smart-apply/default-template";
import { SmartApplyR } from "../../route";
import ReactDatePicker from "react-datepicker";
import dayjs from "dayjs";
import { SmartApplyMenuList } from "../../../../organisms/smart-apply/menu-list";
import { useApplicationRemovalTaskRegisterHook } from "./hook";
import { Redirect, useHistory } from "react-router-dom";
import { ConfirmApplicationRemovalTaskRegister } from "../../../../organisms/smart-apply/application-removal-task/confirm-register";

import { RegisterErrorMessages } from "../../../../../application/domain/application/application-removal-task/create-task-result";
import { RequiredTag } from "../../../../atoms/required-tag";
import { useAuthContextV2 } from "../../../../../auth-state";
import { toast } from "react-toastify";
import { ApplicationsRegisterError } from "../../../../organisms/smart-apply/application-removal-task/register-error/applications-register-error";

export const ApplicationRemovalTaskRegister: React.FC = () => {
  const { auth } = useAuthContextV2();
  const history = useHistory();

  const {
    removeDate,
    setRemoveDate,
    reason,
    setReason,
    file,
    setFile,
    createTaskResult,
    errorCode,
    targetProcedureName,
    removeDateError,
    reasonError,
    validInputs,
    resetResult,
    validate,
    submit,
    isLoading,
  } = useApplicationRemovalTaskRegisterHook();

  if (
    !auth.checkGroupPermissionWithoutTarget([
      "applications.removal_tasks_create",
    ])
  ) {
    toast.error("このページを閲覧する権限がありません。");
    return <Redirect to={SmartApplyR.Applications.link({})} />;
  }

  return (
    <SmartApplyTemplate
      breadcrumbs={[{ to: "#", title: "申請削除" }]}
      menuList={<SmartApplyMenuList />}
      contentsHeader={
        <ContentsHeader
          title={"申請削除タスク登録"}
          caption={[]}
          captionDirection="row"
          buttons={[
            [
              <SecondaryButton
                key={"remove-application"}
                text={"削除タスク一覧に戻る"}
                onClick={() => {
                  history.push(SmartApplyR.ApplicationRemovalTaskList.link({}));
                }}
              />,
            ],
          ]}
        />
      }
    >
      {(!createTaskResult?.ok || errorCode) && (
        <>
          <div className="columns mx-0 mt-2 mb-0">
            <div className="column is-narrow p-0">
              <label htmlFor="remove-date" className="label">
                CSVファイル
              </label>
            </div>
            <div className="column is-narrow p-0">
              <RequiredTag />
            </div>
          </div>
          <p className="help mb-2">
            <div>
              申請一覧画面からダウンロードしたCSVファイルを指定してください。
            </div>
            <div>
              ダウンロード時に「申請ID」を出力項目に含めてください。CSVのヘッダーは削除しないでください。
            </div>
          </p>
          <label className="file-label">
            <input
              className="file-input"
              id="csv-file"
              type="file"
              name="resume"
              accept="text/csv"
              onChange={(e) => {
                const fileReader = new FileReader();
                const fileDropped = e.target.files?.[0];
                if (!fileDropped) {
                  return;
                }
                fileReader.onload = function () {
                  const dataUri = this.result as string;
                  setFile({
                    name: fileDropped.name,
                    data: dataUri.split(",")[1],
                  });
                };
                fileReader.readAsDataURL(fileDropped);
              }}
            />
            <span className="file-cta">
              <span className="file-icon">
                <i className="fas fa-upload" />
              </span>
              <span className="file-label">
                {file ? file.name : "ファイルを選択…"}
              </span>
            </span>
            <span className="file-name">{file ? "選択済み" : "未選択"}</span>
          </label>
          <div className="mt-5">
            <TextField
              label={"削除理由"}
              description={""}
              placeholder={""}
              onChange={(e) => setReason(e.target.value)}
              value={reason}
              errorMessages={reasonError}
              required
            />
          </div>
          <div className="columns mx-0 mt-5 mb-0">
            <div className="column is-narrow p-0">
              <label htmlFor="remove-date" className="label">
                削除予定日
              </label>
            </div>
            <div className="column is-narrow p-0">
              <RequiredTag />
            </div>
          </div>
          <p className="help mb-2">
            <div>削除予定日までは、削除をキャンセルできます。</div>
            <div>
              意図せぬ申請の削除を防ぐため、削除予定日は7日後以降を指定することを推奨します。
            </div>
          </p>
          <ReactDatePicker
            id="remove-date"
            className="input"
            selected={removeDate ? dayjs(removeDate).toDate() : null}
            onChange={(date) => {
              if (date) {
                setRemoveDate(date);
              }
            }}
            placeholderText="----/--/--"
            timeFormat="HH:mm"
            dateFormat="yyyy/MM/dd"
          />
          {removeDateError && (
            <p className="help is-danger">{removeDateError}</p>
          )}
          <div className="mt-5">
            <PrimaryButton
              text={"削除内容を確認する"}
              onClick={validate}
              disabled={!validInputs}
            />
          </div>
        </>
      )}
      {errorCode && (
        <div className="notification is-danger is-light mb-2 mt-4">
          {RegisterErrorMessages[errorCode]}
        </div>
      )}
      {createTaskResult && removeDate && (
        <div className="mt-4">
          {createTaskResult.ok && createTaskResult.summary && (
            <ConfirmApplicationRemovalTaskRegister
              taskSummary={createTaskResult.summary}
              targetProcedureName={targetProcedureName}
              reason={reason}
              removeDate={removeDate}
              onCancel={resetResult}
              onSubmit={submit}
            />
          )}
          {createTaskResult.applicationErrors && (
            <ApplicationsRegisterError
              applicationErrors={createTaskResult.applicationErrors}
            />
          )}
        </div>
      )}
      <Spinner loading={isLoading} />
    </SmartApplyTemplate>
  );
};
